export const ORDER_CARGO_TYPES = {
  1: 'mgt',
  2: 'sgt',
  3: 'kgt',
}

export const ORDER_CROSS_BORDER_TYPES = {
  0: 'local',
  1: 'crossBorder',
}

export const ORDER_STATUSES = ['new', 'in_progress', 'complete']

export const ORDER_CALCULATED_LABELS = [
  'open',
  'fulfilled',
  'partial',
  'canceled',
]

export const ORDER_STATUS_FILTERS = [
  { value: 'new', label: 'statuses.new' },
  { value: 'in_progress', label: 'statuses.in_progress' },
  { value: 'complete', label: 'statuses.complete' },
]

export const ORDER_CALCULATED_LABEL_COLORS = {
  open: 'warning',
  fulfilled: 'success',
  partial: 'warning',
  canceled: 'error',
}

export const ORDER_ORIGIN_TYPES = [
  { value: 'wildberries', label: 'Wildberries' },
  { value: 'ozon', label: 'Ozon' },
  { value: 'yandex', label: 'Yandex' },
  { value: 'lemana_pro', label: 'Lemana Pro' },
]

export function orderStatusLabel(t, id, fallback = '') {
  if (!id) return fallback
  const key = `statuses.${id}`
  const translated = t(key, undefined, { missingWarn: false })
  return translated && translated !== key ? translated : fallback || id
}

export function cargoTypeLabel(t, value) {
  const key = ORDER_CARGO_TYPES[value]
  return key ? t(`pages.orders.cargoType.${key}`) : String(value ?? '-')
}

export function crossBorderTypeLabel(t, value) {
  const key = ORDER_CROSS_BORDER_TYPES[value]
  return key ? t(`pages.orders.crossBorderType.${key}`) : String(value ?? '-')
}

export function shortOriginId(value, length = 14) {
  if (!value) return '-'
  const str = String(value)
  return str.length > length ? `${str.slice(0, length)}…` : str
}

export function apiErrorText(error) {
  const data = error?.data || error?.response?._data || error?.response?.data

  if (data) {
    if (Array.isArray(data.errors) && data.errors.length) {
      const text = data.errors
        .map((item) => item?.title || item?.message)
        .filter(Boolean)
        .join('; ')
      if (text) return text
    }

    if (data.errors && typeof data.errors === 'object') {
      const text = Object.values(data.errors).flat().filter(Boolean).join('; ')
      if (text) return text
    }

    if (typeof data.message === 'string' && data.message) return data.message
  }

  return error?.message || String(error)
}

export function groupOrderItemsByProduct(items = []) {
  const groups = new Map()

  for (const item of items) {
    const product = item.product || {}
    const key = [
      item.productUuid || 'unresolved',
      item.wbCargoType ?? '-',
      item.wbCrossBorderType ?? '-',
      item.wbWarehouseId ?? '-',
    ].join('|')

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        productUuid: item.productUuid || null,
        productName: item.productName || product.name || null,
        article: item.article || product.article || null,
        cargoType: item.wbCargoType ?? null,
        crossBorderType: item.wbCrossBorderType ?? null,
        warehouseId: item.wbWarehouseId ?? null,
        items: [],
      })
    }

    groups.get(key).items.push(item)
  }

  return [...groups.values()]
}

const buildQuery = (params = {}) => {
  const query = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value)) {
      value
        .filter((v) => v !== undefined && v !== null && v !== '')
        .forEach((v) => query.append(key, v))
    } else {
      query.append(key, value)
    }
  }

  const str = query.toString()
  return str ? `?${str}` : ''
}

export const useOrders = () => {
  const getOrders = async (params = {}) => {
    const response = await useApi(`api/orders${buildQuery(params)}`, {
      method: 'GET',
    })
    return response?.data
  }

  const getOrder = async (uuid) => {
    const response = await useApi(`api/orders/${uuid}`, { method: 'GET' })
    return response?.data
  }

  const getOrderItems = async (uuid) => {
    const response = await useApi(`api/orders/${uuid}/items`, {
      method: 'GET',
    })
    return response?.data
  }

  const getGroupedItems = async (params = {}) => {
    const response = await useApi(
      `api/orders/items-grouped${buildQuery(params)}`,
      { method: 'GET' }
    )
    return response?.data
  }

  const createTaskFromOrder = async (orderId) => {
    const response = await useApi(
      'api/warehouse-task/selection/create-from-order',
      {
        method: 'POST',
        body: { orderId },
      }
    )
    return response?.data
  }

  const createTaskFromOrderItems = async (orderItemIds = []) => {
    const response = await useApi(
      'api/warehouse-task/selection/create-from-order-items',
      {
        method: 'POST',
        body: { orderItemIds },
      }
    )
    return response?.data
  }

  return {
    getOrders,
    getOrder,
    getOrderItems,
    getGroupedItems,
    createTaskFromOrder,
    createTaskFromOrderItems,
  }
}
